<template>
  <div class="min-h-screen flex items-center justify-center bg-brand-navy px-4 py-8">
    <div class="bg-white p-8 sm:p-10 rounded-3xl shadow-2xl w-full max-w-md border border-gray-100">

      <!-- Logo & Titre -->
      <div class="text-center mb-8">
        <div class="flex justify-center items-center mb-3">
          <img src="/logo.png" alt="Logo Giraaf" class="h-14 w-auto object-contain"
            @error="$event.target.style.display = 'none'" />
        </div>

        <h1 class="text-2xl sm:text-3xl font-extrabold text-brand-navy tracking-tight">
          Mot de passe <span class="text-brand-purple">oublié ?</span>
        </h1>
        <p class="text-gray-500 text-sm mt-2">
          Entrez votre email pour recevoir les instructions de réinitialisation.
        </p>
      </div>

      <!-- Message de confirmation SI succès -->
      <div v-if="isSubmitted"
        class="bg-green-50 border border-green-200 text-green-700 p-4 rounded-2xl mb-6 text-sm flex flex-col gap-2">
        <div class="flex items-center gap-2 font-semibold">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600" viewBox="0 0 20 20"
            fill="currentColor">
            <path fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd" />
          </svg>
          E-mail envoyé !
        </div>
        <p class="text-xs text-green-600 leading-relaxed">
          Si un compte est associé à <strong>{{ email }}</strong>, les instructions de réinitialisation ont été
          envoyées.
        </p>
        <p class="text-[11px] text-gray-500 italic mt-1">
          (En mode local développement, vérifiez les logs de votre terminal Rails pour voir le lien de
          réinitialisation).
        </p>
      </div>

      <!-- Formulaire -->
      <form v-else class="space-y-5" @submit.prevent="handleForgotPassword">
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
            Adresse Email de votre compte
          </label>
          <input v-model="email" type="email" autocomplete="email" placeholder="vous@exemple.com" required
            class="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none transition text-gray-800 text-sm" />
        </div>

        <!-- Message d'erreur -->
        <div v-if="error" class="p-3 bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl font-medium">
          {{ error }}
        </div>

        <!-- Bouton Dégradé Signature Officiel -->
        <button type="submit" :disabled="isLoading"
          style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
          class="w-full text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:opacity-95 transform active:scale-[0.99] transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          <svg v-if="isLoading" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <span class="text-white font-bold">{{ isLoading ? 'Envoi en cours...' : 'Envoyer les instructions' }}</span>
        </button>
      </form>

      <!-- Lien Retour Connexion -->
      <div class="mt-8 pt-6 border-t border-gray-100 text-center">
        <router-link to="/login"
          class="inline-flex items-center gap-1.5 text-sm text-brand-navy hover:text-brand-purple font-semibold transition">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à la page de connexion
        </router-link>
      </div>

    </div>
  </div>
</template>

<script>
import apiClient from '@/services/apiClient'

export default {
  name: 'ForgotPasswordView',
  data() {
    return {
      email: '',
      isSubmitted: false,
      isLoading: false,
      error: null
    }
  },
  methods: {
    async handleForgotPassword() {
      this.isLoading = true
      this.error = null

      try {
        // Envoi au backend Rails avec headers explicites pour éviter l'erreur 406
        await apiClient.post(
          '/api/v1/password',
          { user: { email: this.email } },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          }
        )

        this.isSubmitted = true
      } catch (err) {
        console.error('Détail erreur forgot-password:', err)

        if (err.code === 'ERR_NETWORK') {
          this.error = 'Serveur Rails injoignable sur le port 3005.'
        } else if (err.response?.status === 404) {
          this.error = 'Aucun utilisateur trouvé avec cette adresse email.'
        } else if (err.response?.status === 406) {
          // Si Rails attend .json dans l'URL, tentative de secours
          try {
            await apiClient.post('/api/v1/password.json', { user: { email: this.email } })
            this.isSubmitted = true
            return
          } catch (retryErr) {
            this.error = 'Le serveur Rails a rejeté le format de la requête (Erreur 406).'
          }
        } else if (err.response?.data?.error) {
          this.error = err.response.data.error
        } else {
          this.error = 'Impossible de traiter la demande pour le moment.'
        }
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>
